import React, { useEffect, useRef, useState } from 'react';

/**
 * Popover Component
 * A floating container that appears relative to a trigger element
 *
 * @param {boolean} isOpen - Whether the popover is visible
 * @param {function} onClose - Callback when popover should close
 * @param {React.ReactNode} children - Popover content
 * @param {React.ReactNode} trigger - Element that triggers the popover
 * @param {string} position - Position relative to trigger: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right'
 */
const Popover = ({
  isOpen,
  onClose,
  children,
  trigger,
  position = 'bottom-left',
  width = 'auto',
  maxWidth = '400px'
}) => {
  const popoverRef = useRef(null);
  const triggerRef = useRef(null);
  const [popoverStyle, setPopoverStyle] = useState({});

  // Handle click outside to close
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        onClose();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Calculate position with smart viewport detection
  useEffect(() => {
    if (!isOpen || !triggerRef.current || !popoverRef.current) return;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const updatePosition = () => {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const popoverHeight = popoverRef.current?.offsetHeight || 400;
      const popoverWidth = popoverRef.current?.offsetWidth || 400;

      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const spacing = 8; // Gap between trigger and popover
      const edgePadding = 16; // Padding from viewport edges

      const newStyle = {};

      // Calculate available space
      const spaceBelow = viewportHeight - triggerRect.bottom - edgePadding;
      const spaceAbove = triggerRect.top - edgePadding;

      // Smart vertical positioning (flip if needed)
      const shouldFlipUp = spaceBelow < popoverHeight && spaceAbove > spaceBelow;

      if (shouldFlipUp) {
        // Open upward
        newStyle.bottom = `${viewportHeight - triggerRect.top + spacing}px`;
      } else {
        // Open downward (default)
        newStyle.top = `${triggerRect.bottom + spacing}px`;
        // Constrain to viewport height
        const maxHeight = Math.min(spaceBelow - spacing, viewportHeight * 0.8);
        newStyle.maxHeight = `${maxHeight}px`;
        newStyle.overflowY = 'auto';
      }

      // Horizontal positioning based on position prop
      if (position.includes('right')) {
        // Align to right edge of trigger
        const rightPos = viewportWidth - triggerRect.right;

        // Check if popover would go off left edge
        if (triggerRect.right - popoverWidth < edgePadding) {
          // Not enough space, align to right edge instead
          newStyle.right = `${edgePadding}px`;
        } else {
          newStyle.right = `${rightPos}px`;
        }
      } else {
        // Align to left edge of trigger (default)
        const leftPos = triggerRect.left;

        // Check if popover would go off right edge
        if (leftPos + popoverWidth > viewportWidth - edgePadding) {
          // Not enough space, align to right of trigger instead
          newStyle.right = `${viewportWidth - triggerRect.right}px`;
        } else {
          newStyle.left = `${leftPos}px`;
        }
      }

      setPopoverStyle(newStyle);
    };

    // Initial position
    updatePosition();

    // Update on scroll/resize
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, position]);

  return (
    <>
      {/* Trigger element */}
      <div ref={triggerRef} style={{ display: 'inline-block' }}>
        {trigger}
      </div>

      {/* Popover content */}
      {isOpen && (
        <div
          ref={popoverRef}
          style={{
            position: 'fixed',
            zIndex: 1000,
            width: width,
            maxWidth: maxWidth,
            backgroundColor: 'var(--dark-surface)',
            border: '1px solid var(--gray-600)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)',
            padding: 'var(--space-4)',
            ...popoverStyle,
            animation: 'popoverFadeIn 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          <div style={{
            maxHeight: 'inherit',
            overflowY: popoverStyle.overflowY || 'visible',
            overflowX: 'hidden',
          }}>
            {children}
          </div>
        </div>
      )}

      {/* CSS Animation */}
      <style>
        {`
          @keyframes popoverFadeIn {
            from {
              opacity: 0;
              transform: translateY(-10px) scale(0.96);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
        `}
      </style>
    </>
  );
};

export default Popover;
